import Flickity from 'flickity';

const focusNodes = {
  INPUT: true,
  TEXTAREA: true,
  SELECT: true,
};

let documentHandlersAssigned = false;

function assignDocumentHandlers() {
  if (documentHandlersAssigned) return;

  documentHandlersAssigned = true;

  // https://github.com/metafizzy/flickity/issues/959
  let touchingCarousel = false;
  let touchStartCoords;

  document.body.addEventListener('touchstart', function (e) {
    if (e.target.closest('.flickity-slider')) {
      touchingCarousel = true;
    } else {
      touchingCarousel = false;
      return;
    }

    touchStartCoords = {
      x: e.touches[0].pageX,
      y: e.touches[0].pageY,
    };
  });

  document.body.addEventListener(
    'touchmove',
    function (e) {
      if (!(touchingCarousel && e.cancelable)) {
        return;
      }

      const moveVector = {
        x: e.touches[0].pageX - touchStartCoords.x,
        y: e.touches[0].pageY - touchStartCoords.y,
      };

      if (Math.abs(moveVector.x) > 7) {
        e.preventDefault();
      }
    },
    { passive: false },
  );
}

class PatchedFlickity extends Flickity {
  get options() {
    return this._options;
  }

  set options(options) {
    this._options = new Proxy(options, {
      get(target, name, receiver) {
        if (!Reflect.has(target, name)) {
          return undefined;
        }
        let rv = Reflect.get(target, name, receiver);
        if (typeof rv === 'function') {
          rv = rv();
        }
        return rv;
      },
      set(target, name, value, receiver) {
        return Reflect.set(target, name, value, receiver);
      },
    });
  }

  constructor(...args) {
    assignDocumentHandlers();
    super(...args);
    this.on('settle', (e, idx) => {
      if (this.resizeQueued) {
        this.resizeQueued = false;
        this.resize();
      }
    });
  }

  pointerDownFocus(e) {
    const isFocusNode = focusNodes[e.target.nodeName];
    if (!isFocusNode) {
      const actualPosition = window.scrollY || window.pageYOffset;
      this.focus();
      // Reset scroll position as some browsers jump to the focussed element.
      // There is a spec for passing preventScroll: true to focus, but it isn't
      // supported on some browsers
      setTimeout(function () {
        window.scroll(window.scrollX || window.pageXOffset, actualPosition);
      }, 0);
    }
  }

  updateDraggable() {
    super.updateDraggable();
    // Use this rather than relying on Flickity's is-draggable class since
    // that can be manually disabled. This allows us to make an undraggable
    // carousel with visible controls.
    if (this.slidesWidth) {
      this.element.classList.add('show-controls');
    } else {
      this.element.classList.remove('show-controls');
      // Flickity doesn't account for the situation where cells are not grouped
      // but do not fill .flickity-viewport (eg. two 50% width cells with
      // contain: true), possibly because it doesn't updateDraggable on resize
      // by default.
      this.element.classList.remove('is-draggable');
      this.isDraggable = false;
    }
  }

  resize() {
    // Ignore resize events triggered while dragging, since Safari on iOS15+
    // can change the height of the nav bar while dragging
    if (this.isAnimating) {
      this.resizeQueued = true;
      return;
    }

    this.cells.forEach((cell) => {
      cell.element.style.minHeight = 0;
      cell.element.style.maxHeight = 'none';
    });
    super.resize();
    this.cells.forEach((cell) => {
      cell.element.style.minHeight = null;
      cell.element.style.maxHeight = null;
    });
    if (this.slides) {
      this.updateDraggable();
    }
  }
}

export default PatchedFlickity;
